import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/Toast';

function Dashboard() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();
  const { admin, token, logout, loading: authLoading } = useAuth();
  const { notify, confirm } = useToast();

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const testsRes = await axios.get('/api/tests', { headers: { Authorization: `Bearer ${token}` } });
        setTests(testsRes.data);
      } catch (err) {
        notify('Failed to load data', 'error');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token, notify]);

  const copyLink = async (slug, testId) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/exam/${slug}`);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = `${window.location.origin}/exam/${slug}`;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedId(testId);
    notify('Link copied to clipboard', 'success');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteTest = (testId, e) => {
    e.stopPropagation();
    confirm('Are you sure you want to delete this test? All associated submissions will remain but the test link will stop working.', async () => {
      try {
        await axios.delete(`/api/tests/${testId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTests(tests.filter(t => t._id !== testId));
        notify('Test deleted successfully', 'success');
      } catch {
        notify('Failed to delete test', 'error');
      }
    });
  };

  if (authLoading || loading) {
    return (
      <div className="admin-layout">
        <aside className="sidebar">
          <div className="sidebar-header"><h2>Exam System</h2></div>
        </aside>
        <main className="admin-main">
          <div className="loading-spinner">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Exam System</h2>
        </div>
        <nav className="sidebar-nav">
          <Link to="/admin/dashboard" className="nav-link active">Dashboard</Link>
          <Link to="/admin/create-test" className="nav-link">Create Test</Link>
          <Link to="/admin/candidates" className="nav-link">Candidates</Link>
        </nav>
        <div className="sidebar-footer">
          <div className="admin-info">
            <span className="admin-name">{admin?.name || admin?.email}</span>
          </div>
          <button className="btn btn-logout" onClick={logout}>Logout</button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-header">
          <h1>Dashboard</h1>
          <Link to="/admin/create-test" className="btn btn-primary">Create New Test</Link>
        </div>

        {tests.length === 0 ? (
              <div className="empty-state">
                <h3>No tests created yet</h3>
                <p>Create your first screening test to get started.</p>
                <Link to="/admin/create-test" className="btn btn-primary">Create Test</Link>
              </div>
            ) : (
              <div className="test-grid">
                {tests.map(test => (
                  <div
                    key={test._id}
                    className="test-card"
                    onClick={() => navigate(`/admin/test/${test._id}`)}
                  >
                    <div className="test-card-header">
                      <h3>
                        {test.title}
                        {!test.isActive && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#e74c3c', fontWeight: 'normal' }}>(Inactive)</span>
                        )}
                      </h3>
                      {test.createdBy === admin?.id && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => handleDeleteTest(test._id, e)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    <div className="test-card-body">
                      <div className="test-stat">
                        <span className="stat-label">Questions</span>
                        <span className="stat-value">{test.questions?.length || 0}</span>
                      </div>
                      <div className="test-stat">
                        <span className="stat-label">Duration</span>
                        <span className="stat-value">{test.duration} min</span>
                      </div>
                      <div className="test-stat">
                        <span className="stat-label">Passing %</span>
                        <span className="stat-value">{test.passingPercentage}%</span>
                      </div>
                    </div>
                    <div className="test-card-footer">
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyLink(test.shareableSlug, test._id);
                        }}
                      >
                        {copiedId === test._id ? 'Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </main>
    </div>
  );
}

export default Dashboard;
